/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { FlatList, TouchableOpacity, View } from "react-native";
import { useLazyQuery } from "@apollo/client";
import { info } from "react-icons-kit/feather/info";
import { check } from "react-icons-kit/fa/check";

import {
  GetSummitDetailQuery,
  GetUserSummitSessionListQuery,
  SkillType,
  SummitType,
} from "graphql/internal/generated";
import {
  GET_SUMMIT_DETAIL,
  GET_USER_SUMMIT_SESSION_LIST,
} from "graphql/internal/Queries";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { getNodes } from "config/Utilities";
import { LoadingDots, MediaQuery, Text } from "UpduoLibrary";
import {
  upduoPrimaryBlack,
  upduoPrimaryBlue,
  upduoDarkGray,
  upduoLighterGray,
  upduoLightGray,
} from "config/UpduoColors";
import Icon from "react-icons-kit";

function getSkills(summit: GetSummitDetailQuery["userSummit"]) {
  return summit
    ? summit.skillModules
        .map((skillModule) => getNodes(skillModule.activeSkillList))
        .flat()
    : [];
}

export default React.memo(function SummitTopicList({
  summit,
  onSelectSkill,
}: {
  summit: SummitType;
  onSelectSkill: (skill: SkillType) => void;
}) {
  const [summitSkillList, setSummitSkillList] = useState(getSkills(summit));
  const [completedSessions, setCompletedSessions] = useState<Set<string>>(
    new Set()
  );

  const [
    getUserSummitSessionList,
    { loading: getUserSummitSessionListLoading },
  ] = useLazyQuery<GetUserSummitSessionListQuery>(
    GET_USER_SUMMIT_SESSION_LIST,
    {
      fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
      onCompleted: ({ skalesessions }) => {
        const userSummitSessionList = getNodes(skalesessions);
        const completedSessions = new Set(
          userSummitSessionList.map((x) => x.skill.id)
        );
        setCompletedSessions(completedSessions);
      },
      onError: (error) => {
        console.error("ERROR getUserSummitSessionList", error);
        UpduoAnalytics.logEvent("app-summit-getusersummitsessionlist-error", {
          error,
        });
      },
    }
  );

  const [getSummitDetail, { loading: getSummitDetailLoading }] =
    useLazyQuery<GetSummitDetailQuery>(GET_SUMMIT_DETAIL, {
      fetchPolicy: "network-only",
      onCompleted: ({ userSummit }) => {
        if (userSummit) {
          setSummitSkillList(getSkills(userSummit));
        }
      },
    });

  useEffect(() => {
    getUserSummitSessionList({
      variables: {
        getUserSummitSessionListRequest: {
          summitId: summit.id || summit["summitId"],
        },
      },
    });
  }, []);

  useEffect(() => {
    if (!summitSkillList.length) {
      getSummitDetail({
        variables: { getSummitRequest: { summitId: summit.id } },
      });
    }
  }, [summitSkillList]);

  return (
    <View
      style={{
        paddingVertical: 14,
        paddingHorizontal: 24,
      }}
    >
      {getUserSummitSessionListLoading || getSummitDetailLoading ? (
        <View
          style={{
            flexDirection: "row",
            flex: 1,
            justifyContent: "center",
          }}
        >
          <LoadingDots color={upduoLightGray} size={10} />
        </View>
      ) : (
        <FlatList
          getItemLayout={(data, index) => ({
            length: 0,
            offset: 0 * index,
            index,
          })}
          scrollEnabled={false}
          keyExtractor={(_, index) => String(index)}
          data={summitSkillList}
          renderItem={({ item }) => (
            <SummitSkillListItem
              skill={item}
              hasCompletedSkill={completedSessions.has(item.id)}
              onClick={() => {
                onSelectSkill(item as SkillType);
              }}
            />
          )}
          ItemSeparatorComponent={() => (
            <View
              style={{
                marginVertical: 14,
                marginLeft: 25,
                borderTopWidth: 1,
                borderColor: upduoLighterGray,
              }}
            />
          )}
        />
      )}
    </View>
  );
});

function SummitSkillListItem({
  skill,
  hasCompletedSkill,
  onClick,
}: {
  skill: any;
  hasCompletedSkill: boolean;
  onClick: () => void;
}) {
  const isPublished = skill.isPublished;
  let skillTextColor = upduoPrimaryBlack;
  let skillBulletPointColor = upduoPrimaryBlue;
  let skillBulletPointCheckColor = upduoPrimaryBlue;
  if (isPublished) {
    if (hasCompletedSkill) {
      skillTextColor = upduoPrimaryBlue;
      skillBulletPointColor = upduoPrimaryBlue;
      skillBulletPointCheckColor = upduoPrimaryBlue;
    } else {
      skillTextColor = upduoDarkGray;
      skillBulletPointColor = upduoLightGray;
      skillBulletPointCheckColor = upduoLightGray;
    }
  } else {
    skillTextColor = upduoLightGray;
    skillBulletPointColor = upduoLightGray;
    skillBulletPointCheckColor = upduoLightGray;
  }

  return (
    <TouchableOpacity
      style={{
        flexDirection: "row",
        alignItems: "center",
        marginTop: 4,
      }}
      key={skill.id}
      onPress={onClick}
    >
      <View
        style={{
          marginRight: 8,
          // bullet point color
          backgroundColor: skillBulletPointColor,
          width: 18,
          height: 18,
          borderRadius: 9,
        }}
      >
        <Text
          style={{
            // skill check icon color
            color: skillBulletPointCheckColor,
            marginTop: -1,
            textAlign: "center",
          }}
        >
          <Icon size={11} icon={check} />
        </Text>
      </View>
      <View
        style={{
          maxWidth: 350,
        }}
      >
        <Text
          style={{
            fontFamily: "ObjectSans-Medium",
            fontWeight: "bold",
            fontSize: 16,
            color: skillTextColor,
          }}
        >
          {skill.name}
        </Text>
      </View>
      <Text
        style={{
          color: upduoPrimaryBlue,
          paddingLeft: 10,
          marginTop: -1,
        }}
      >
        <Icon size={18} icon={info} />
      </Text>
      <View style={{ flexGrow: 1, flexShrink: 1 }} />
      <MediaQuery minWidth={1400}>
        <Text
          style={{
            fontSize: 14,
            color: hasCompletedSkill ? upduoPrimaryBlue : upduoLightGray,
          }}
        >
          {hasCompletedSkill
            ? "completed"
            : `about ${skill.suggestedTotalMinutes} minutes`}
        </Text>
      </MediaQuery>
    </TouchableOpacity>
  );
}
